import { useRef, useState } from "react";
import Button from "../../basic/Button";
import TaskHeader from "./components/TaskHeader";
import "./styles/tasks.scss";
import { useTasks } from "../../../hooks/useTasks";
import Loader from "../../basic/Loader";
import { useEffect } from "react";
import { useCallback } from "react";
import { navigate } from "@reach/router";
import TaskSwitcher from "./TaskSwitcher";
import { reactifyBR } from "../../../utils/stringUtils";
import { markTaskCompleted, isTaskCompleted, SKIP_INTRO_TASKS } from "../../../helpers/taskGates";
import { useUser } from "../../../hooks/useApi";
import {
  createEventAttemptId,
  EVENT_TYPES,
  reportEvent,
} from "../../../services/eventEmitter";

const STEP_INIT = 0;
const STEP_1 = 1;
const STEP_2 = 2;
const STEP_END = STEP_2;
const TASK_TYPES_WITH_INTERNAL_CONTINUE = ["memory", "slide", "merge"];
const PRODUCTION_TASK_REDIRECT_HOSTS = [
  "millsonicbox.com",
  "www.millsonicbox.com",
  "sonicboxmusic.com",
  "www.sonicboxmusic.com",
];

const resolveTaskRedirectUrl = (url) => {
  if (typeof url !== "string" || url.trim() === "") {
    return null;
  }

  if (typeof window === "undefined") {
    return url;
  }

  try {
    const targetUrl = new URL(url, window.location.origin);
    const isDevelopmentRuntime =
      process.env.NODE_ENV !== "production" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    if (
      isDevelopmentRuntime &&
      PRODUCTION_TASK_REDIRECT_HOSTS.includes(targetUrl.hostname)
    ) {
      return `${window.location.origin}${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
    }

    return targetUrl.toString();
  } catch (error) {
    console.log("resolveTaskRedirectUrl error", error);
    return url;
  }
};

const getInternalPath = (url) => {
  if (typeof url !== "string" || typeof window === "undefined") {
    return null;
  }

  try {
    const targetUrl = new URL(url, window.location.origin);
    if (targetUrl.origin !== window.location.origin) {
      return null;
    }

    return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
  } catch (error) {
    return url.startsWith("/") ? url : null;
  }
};

export default function Task({
  name,
  radioid,
  nameseo,
  radioimage,
  onCompleted = null,
}) {
  const skipsIntro = SKIP_INTRO_TASKS.includes(nameseo);
  const [step, setStep] = useState(skipsIntro ? STEP_1 : STEP_INIT);
  const [values, setValues] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [failed, setFailed] = useState(false);
  const {
    tasks,
    isLoading,
    isError,
    error,
    submitTask,
    resetSubmitTask,
    submitLoading,
    submitIsError,
    submitError,
    submitData,
    submitAttemptId,
    currentTask,
    setCurrentTask,
  } = useTasks(nameseo);
  const [noReturn, setNoReturn] = useState(false);
  const reportedSubmitRef = useRef(null);
  const { user } = useUser();
  const taskHasInternalContinue = TASK_TYPES_WITH_INTERNAL_CONTINUE.includes(
    currentTask?.type
  );

  useEffect(() => {
    if (skipsIntro && tasks?.tasks?.[0] && !currentTask) {
      setCurrentTask(tasks.tasks[0]);
    }
  }, [tasks, skipsIntro, currentTask, setCurrentTask]);

  useEffect(() => {
    if (submitData?.status === "success") {
      setNoReturn(true);
      if (nameseo && !isTaskCompleted(nameseo, user)) {
        markTaskCompleted(nameseo, user);
      }
    }
  }, [submitData, nameseo, user]);

  useEffect(() => {
    if (
      submitData?.status !== "success" ||
      !submitAttemptId ||
      reportedSubmitRef.current === submitData
    ) {
      return;
    }

    reportedSubmitRef.current = submitData;
    const passed = currentTask?.checkcorrect ? !failed : null;

    void reportEvent({
      type: EVENT_TYPES.TASK_RESULT,
      user,
      radio: {
        radioid,
        radioname: name,
        nameseo,
      },
      payload: {
        submitted: true,
        failed,
        passed,
        values,
        task: currentTask,
        response: submitData,
      },
      attemptId: submitAttemptId,
    });
  }, [
    submitAttemptId,
    submitData,
    currentTask,
    failed,
    name,
    nameseo,
    radioid,
    user,
    values,
  ]);

  const handlePrevStep = () => {
    setStep(Math.max(skipsIntro ? STEP_1 : STEP_INIT, step - 1));
  };

  const handleNextStep = () => {
    if (submitLoading || noReturn) {
      return;
    }

    if (step === STEP_INIT) {
      if (tasks.tasks.length === 1) {
        // console.log("setCurrentTask", tasks.tasks[0]);
        setCurrentTask(tasks.tasks[0]);
      }
    }
    if (step === 1) {
      // console.log("submitTask", values);
      // console.log("task", currentTask);
      // console.log("tasks", tasks);
      if (currentTask?.checkcorrect) {
        const failed = checkFailed(currentTask.checkcorrect, values);
        setFailed(failed);
      }
      const eventAttemptId = createEventAttemptId();
      submitTask({
        radioid: radioid,
        data: JSON.stringify(values),
        eventAttemptId,
      });
    }
    setStep(Math.min(STEP_END, step + 1));
  };

  const handleRestart = () => {
    resetSubmitTask();
    setStep(skipsIntro ? STEP_1 : STEP_INIT);
    setValues({});
    setIsValid(false);
    setFailed(false);
    setNoReturn(false);
  };

  const checkFailed = (correct, values) => {
    let failed = false;
    Object.keys(correct).forEach((key) => {
      // console.log(correct[key], values[key]);
      if (correct[key] !== values[key]) {
        failed = true;
      }
    });
    return failed;
  };

  const handleLastStep = useCallback(() => {
    // console.log("last step");
    if (submitData?.data?.url) {
      const redirectUrl = resolveTaskRedirectUrl(submitData?.data?.url);
      if (redirectUrl) {
        const internalPath = getInternalPath(redirectUrl);
        if (onCompleted) {
          onCompleted({ path: internalPath || redirectUrl, source: "task_redirect" });
          return;
        }

        window.location.href = redirectUrl;
        return;
      }
    }

    if (nameseo && isTaskCompleted(nameseo, user)) {
      if (onCompleted) {
        onCompleted({ path: `/sonicbox/${nameseo}`, source: "task_completed" });
        return;
      }

      navigate(`/sonicbox/${nameseo}`);
      return;
    }

    if (onCompleted) {
      onCompleted({ path: "/sonicboxes", source: "task_fallback" });
      return;
    }

    navigate("/sonicboxes");
  }, [submitData, nameseo, onCompleted, user]);

  return (
    <>
      <TaskHeader
        name={name}
        radioimage={radioimage}
        large={step === STEP_INIT}
      />
      {(isError && <p className="alert alert-warning">{error.message}</p>) ||
        (!isLoading && !tasks && (
          <>
            <p className="alert alert-warning">No task found</p>
            <p>{nameseo}</p>
          </>
        )) ||
        (step === STEP_INIT && (
          <>
            <h2>¡Bienvenido!</h2>
            {isLoading && <Loader />}
            <p>{tasks?.message}</p>
          </>
        )) ||
        (step === STEP_1 && (
          <TaskSwitcher
            type={currentTask?.type}
            task={currentTask}
            values={values}
            setValues={setValues}
            setIsValid={setIsValid}
            onSuccess={handleNextStep}
          />
        )) ||
        (step === STEP_2 && null) || (
          <div>
            <h2>
              {tasks?.successtitle
                ? tasks.successtitle
                : "¡Gracias por tu opinión!"}
            </h2>
            {submitLoading && <Loader />}
            {submitIsError && (
              <div className="alert alert-danger">
                {submitError?.message || "Ocurrió un error"}
              </div>
            )}
            {!submitLoading && !submitIsError && submitData?.data?.qr && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <p style={{ marginTop: 0, marginBottom: 20, textAlign: "center" }}>
                  {tasks?.failmessage && failed
                    ? reactifyBR(tasks.failmessage)
                    : reactifyBR(tasks?.successmessage)}
                </p>
                <img
                  src={
                    tasks?.substituteqr
                      ? tasks.substituteqr
                      : submitData.data.qr
                  }
                  alt="QR"
                  style={{ width: 200, marginBottom: 20, borderRadius: 10 }}
                />
                <Button className={"success accent"} onClick={handleLastStep}>
                  {tasks?.redeemtext ? tasks.redeemtext : "Canjear créditos"}
                </Button>
              </div>
            )}
          </div>
        )}
      {(step === STEP_INIT && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: 300,
            margin: "10px auto 0",
          }}
        >
          {tasks?.tasks.length > 1 &&
            tasks?.tasks.map((task, index) => (
              <Button
                key={index}
                className="success"
                onClick={() => {
                  setCurrentTask(task);
                  handleNextStep();
                }}
                style={{ marginBottom: 10 }}
              >
                {task.title}
              </Button>
            ))}
        </div>
      )) ||
        null}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "1rem",
          gap: "1rem",
        }}
      >
        {step !== (skipsIntro ? STEP_1 : STEP_INIT) && !noReturn && (
          <Button
            className={"accent"}
            as="transparent"
            onClick={handlePrevStep}
            style={{ marginRight: 20 }}
          >
            Volver
          </Button>
        )}
        {step === STEP_END && (
          <Button className={"accent"} as="transparent" onClick={handleRestart}>
            Volver a empezar
          </Button>
        )}
        {!isLoading &&
          !(step !== STEP_1 && !!tasks?.tasks && tasks?.tasks.length > 1) &&
          !(step === STEP_1 && taskHasInternalContinue) &&
          !!tasks &&
          ((step !== STEP_END && (
            <Button
              className={"success"}
              onClick={handleNextStep}
              disabled={submitLoading || (step === STEP_1 && !isValid)}
            >
              Continuar
            </Button>
          )) || (
            <Button className={"success"} onClick={handleLastStep}>
              Finalizar
            </Button>
          ))}
        {isError && <p className="alert alert-warning">{error.message}</p>}
      </div>
      {!!tasks && (
        <p>
          {skipsIntro ? step - STEP_1 + 1 : step + 1} de{" "}
          {skipsIntro ? STEP_END : STEP_END + 1}
        </p>
      )}
    </>
  );
}
